import type { SelectionResult } from "@/hooks/firestore/models/selection";
import type {
  ProcessedPickemSelection,
  ProcessedSameEventMultiOutcome,
  ProcessedSelection,
} from "@/hooks/firestore/betting/useEntries";

import { isAfter } from "date-fns/isAfter";
import { isPast } from "date-fns/isPast";
import { subDays } from "date-fns/subDays";
import { exoticPositionFromOutcomeMap } from "@/features/race/util/exotics";
import { navigate } from "@/utilities/navigate";
import { isOutright } from "@/utilities/sharedBettingUtilities";
import {
  isPickemsSelection,
  isSameEventMultiSelection,
  isSingleSelection,
} from "./type-guards";

export function getSelectionMarketStatus(selection: ProcessedSelection) {
  if (isSingleSelection(selection)) {
    return selection.marketStatus;
  } else {
    return Object.values(selection.outcomes)[0].marketStatus;
  }
}

export function getSelectionMarketId(
  selection: ProcessedSelection | ProcessedPickemSelection,
) {
  if (isPickemsSelection(selection) || isSingleSelection(selection)) {
    return selection.marketId;
  }

  return Object.values(selection.outcomes)[0].marketId;
}

export const selectionToUrl = (
  selection: ProcessedSelection,
): string | undefined => {
  if (
    selection.event.eventType === "RACE" &&
    selection.event.attributes?.meetingId
  ) {
    return `/racing/betting/race/${selection.event.attributes.meetingId}/${selection.event.id}/`;
  }

  const hubPrefix = selection.event.hub === "sports" ? "/sports" : "";

  if (selection.event.eventType === "MATCH") {
    return `${hubPrefix}/betting/match/${selection.event.eventId}`;
  }

  if (isOutright(selection.event.eventType)) {
    return `${hubPrefix}/betting/futures/${selection.event.eventId}`;
  }
};

export const getSelectionClickHandler = (
  selection: ProcessedSelection,
): (() => void) | undefined => {
  const url = selectionToUrl(selection);
  if (!url) return;

  const isRecentEnough = selection.event.scheduledStartTime
    ? isAfter(
        new Date(selection.event.scheduledStartTime),
        subDays(new Date(), 90),
      )
    : true;

  if (!isRecentEnough) return;

  return () => navigate(url);
};

export const getSelectionParentName = (selection: ProcessedSelection) => {
  const attributes = selection.event.attributes;
  const isRaceEvent = selection.event.eventType === "RACE";

  if (isSameEventMultiSelection(selection)) {
    return isRaceEvent
      ? `${attributes?.venueName} - R${attributes?.raceNumber}`
      : attributes?.seasonName;
  }

  return isRaceEvent
    ? `${attributes?.venueName} - R${attributes?.raceNumber}`
    : attributes?.seasonName ?? attributes?.tournamentName ?? null;
};

export const getSelectionStatus = (
  selection: ProcessedSelection | ProcessedPickemSelection,
  subOutcomeResult?: SelectionResult,
): {
  message: string;
  variant: "neutral" | "success" | "danger" | "warning";
} | null => {
  const result = subOutcomeResult ?? selection.result;

  const colorMap = {
    WIN: "success",
    LOSE: "neutral",
    UNDECIDED: "warning",
    VOID: "neutral",
  } as const;

  const marketStatus = !isPickemsSelection(selection)
    ? getSelectionMarketStatus(selection)
    : "";

  if (result === "UNDECIDED") {
    // entries is a snapshot, marketStatus can lag past the real start time.
    // Skip outrights though - they run for weeks past their nominal start.
    const hasStarted =
      !isOutright(selection.event.eventType) &&
      selection.event.scheduledStartTime
        ? isPast(new Date(selection.event.scheduledStartTime))
        : false;

    if (
      ["DEACTIVATED", "SUSPENDED", "JUMPED", "LIVE"].includes(marketStatus) ||
      hasStarted
    ) {
      return {
        message: "LIVE",
        variant: "warning",
      };
    }

    return null;
  }

  return {
    message: result,
    variant: colorMap[result] || "neutral",
  };
};

const addToAccumulator =
  <T extends ProcessedSameEventMultiOutcome>(acc: T[][]) =>
  (pos: number, outcome: T) => {
    if (!acc[pos]) {
      acc[pos] = [];
    }

    if (!acc[pos].some((o) => o.outcomeId === outcome.outcomeId)) {
      acc[pos].push(outcome);
    }
  };

export const groupRunnersByPosition = (
  selectedOutcomes: Record<string, ProcessedSameEventMultiOutcome>,
): ProcessedSameEventMultiOutcome[][] => {
  return Object.values(selectedOutcomes)
    .sort((a, b) => {
      const aPosition = exoticPositionFromOutcomeMap.get(a.outcomeType) || 0;
      const bPosition = exoticPositionFromOutcomeMap.get(b.outcomeType) || 0;
      return aPosition - bPosition;
    })
    .reduce((acc, outcome) => {
      const { outcomeType } = outcome;
      const position = exoticPositionFromOutcomeMap.get(outcomeType);
      const addRunnerToPosition = addToAccumulator(acc);

      if (outcomeType === "RUNNER_TOP_2") {
        // this is a quinella edge case
        // top two means both first and second so to need to duplicate the same
        // numbers for first position
        addRunnerToPosition(1, outcome);
      }

      addRunnerToPosition(position || 1, outcome);
      return acc;
    }, []);
};

export const isSelectionSP = (selection: ProcessedSelection): boolean => {
  return selection.event.eventType === "RACE" && selection.odds === 1;
};

const SRM_MARKET_TYPE_PRIORITY: Array<
  ProcessedSameEventMultiOutcome["outcomeType"]
> = ["RUNNER_WIN", "RUNNER_TOP_2", "RUNNER_TOP_3", "RUNNER_TOP_4"];

export const sortSrmOutcomes = (
  outcomes: Record<string, ProcessedSameEventMultiOutcome>,
) => {
  const arr = Object.values(outcomes);
  if (!arr.some((outcome) => outcome.outcomeType)) return arr;

  return arr.sort(
    (a, b) =>
      SRM_MARKET_TYPE_PRIORITY.indexOf(a.outcomeType) -
      SRM_MARKET_TYPE_PRIORITY.indexOf(b.outcomeType),
  );
};

export const sortSgmOutcomes = (
  outcomes: Record<string, ProcessedSameEventMultiOutcome>,
) => {
  const arr = Object.values(outcomes);

  return arr.sort((a, b) => a.outcomeId.localeCompare(b.outcomeId));
};
